// src/components/payment/widgets/CloudPaymentsWidget.jsx

import React, { useState } from 'react';
import { CreditCard, Shield, Smartphone, Loader, CheckCircle, XCircle, AlertCircle } from 'lucide-react';

const CloudPaymentsWidget = ({
                                 amount,
                                 orderId,
                                 onPayment,
                                 onRetry,
                                 onCancel,
                                 status = 'ready',
                                 error = null,
                                 testMode = false,
                                 provider // экземпляр CloudPaymentsProvider
                             }) => {
    const [selectedMethod, setSelectedMethod] = useState('card');

    // Cloud Payments поддерживает разные методы оплаты
    const paymentMethods = [
        { id: 'card', name: 'Банковская карта', icon: CreditCard, description: 'Visa, Mastercard, МИР' },
        { id: 'sbp', name: 'СБП', icon: Smartphone, description: 'Система быстрых платежей' }
    ];

    // Состояние готовности к оплате
    if (status === 'ready') {
        return (
            <div className="space-y-6">
                {/* Тестовый режим */}
                {testMode && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <div className="flex items-start">
                            <AlertCircle className="text-yellow-500 mr-3 mt-0.5" size={18} />
                            <div>
                                <p className="text-sm font-medium text-yellow-800">
                                    Тестовый режим Cloud Payments
                                </p>
                                <p className="text-xs text-yellow-700 mt-1">
                                    Используйте тестовые карты: 5555 5555 5555 4444 (MasterCard)
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Выбор метода оплаты */}
                <div className="grid grid-cols-2 gap-2">
                    {paymentMethods.map(method => {
                        const Icon = method.icon;
                        const isSelected = selectedMethod === method.id;

                        return (
                            <button
                                key={method.id}
                                onClick={() => setSelectedMethod(method.id)}
                                className={`p-3 border-2 rounded-lg transition-all ${
                                    isSelected
                                        ? 'border-blue-500 bg-blue-50'
                                        : 'border-gray-200 hover:border-gray-300'
                                }`}
                            >
                                <Icon className={`mx-auto mb-1 ${
                                    isSelected ? 'text-blue-600' : 'text-gray-600'
                                }`} size={24} />
                                <span className={`text-sm font-medium ${
                                    isSelected ? 'text-blue-600' : 'text-gray-700'
                                }`}>
                                    {method.name}
                                </span>
                                <p className="text-xs text-gray-500 mt-0.5">{method.description}</p>
                            </button>
                        );
                    })}
                </div>

                {/* Пояснение для СБП */}
                {selectedMethod === 'sbp' && (
                    <div className="bg-gray-50 p-4 rounded-lg text-center">
                        <Smartphone className="mx-auto text-blue-500 mb-2" size={32} />
                        <p className="text-sm text-gray-600 mb-2">
                            Оплата через Систему быстрых платежей
                        </p>
                        <p className="text-xs text-gray-500">
                            После нажатия кнопки откроется окно оплаты<br />
                            в приложении вашего банка
                        </p>
                    </div>
                )}

                {/* Информация о комиссиях */}
                <div className="bg-blue-50 p-3 rounded-lg">
                    <div className="flex justify-between text-sm">
                        <span className="text-blue-800">Сумма к оплате:</span>
                        <span className="font-bold text-blue-800">{amount} ₽</span>
                    </div>
                    {provider?.config?.commission && (
                        <div className="flex justify-between text-xs text-blue-600 mt-1">
                            <span>Комиссия {provider.config.commission}%</span>
                        </div>
                    )}
                </div>

                {/* Кнопка оплаты — передаём выбранный метод */}
                <button
                    onClick={() => onPayment({ method: selectedMethod })}
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-3 px-4 rounded-lg font-medium transition-all flex items-center justify-center shadow-md"
                >
                    <CreditCard className="mr-2" size={20} />
                    Оплатить {amount} ₽
                </button>

                {/* Поддерживаемые карты */}
                <div className="flex items-center justify-center space-x-4">
                    <img src="/visa.svg" alt="Visa" className="h-6 opacity-70" />
                    <img src="/mastercard.svg" alt="Mastercard" className="h-6 opacity-70" />
                    <img src="/mir.svg" alt="МИР" className="h-6 opacity-70" />
                </div>

                {/* 3D-Secure */}
                <div className="flex items-center justify-center text-xs text-gray-500">
                    <Shield className="mr-1 text-green-500" size={14} />
                    <span>Защита 3D-Secure</span>
                </div>
            </div>
        );
    }

    // 3D-Secure аутентификация
    if (status === '3dsecure') {
        return (
            <div className="flex flex-col items-center justify-center py-8">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4 w-full">
                    <div className="flex items-center">
                        <Shield className="text-yellow-500 mr-2" size={24} />
                        <div>
                            <p className="text-sm font-medium text-yellow-800">
                                3D-Secure аутентификация
                            </p>
                            <p className="text-xs text-yellow-700">
                                Банк эмитент запрашивает подтверждение
                            </p>
                        </div>
                    </div>
                </div>

                {/* Здесь может быть iframe от банка */}
                <div className="w-full h-48 bg-gray-100 rounded-lg flex items-center justify-center">
                    <p className="text-gray-500">Ожидание подтверждения от банка...</p>
                </div>

                <p className="text-xs text-gray-500 mt-4">
                    Подтвердите операцию в приложении вашего банка
                </p>
            </div>
        );
    }

    // Обработка
    if (status === 'processing') {
        return (
            <div className="flex flex-col items-center justify-center py-12">
                <div className="relative">
                    <Loader className="animate-spin text-blue-500 mb-4" size={48} />
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                    </div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Обработка платежа
                </h3>
                <p className="text-gray-600 text-center">
                    Пожалуйста, подождите...
                </p>
                <p className="text-sm text-gray-500 mt-4">
                    Не закрывайте окно
                </p>
            </div>
        );
    }

    // Успех
    if (status === 'success') {
        return (
            <div className="flex flex-col items-center justify-center py-8">
                <div className="relative mb-4">
                    <CheckCircle className="text-green-500" size={64} />
                    <div className="absolute -inset-2 bg-green-100 rounded-full animate-ping opacity-20"></div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                    Оплата прошла успешно!
                </h3>
                <p className="text-gray-600 text-center mb-4">
                    Заказ #{orderId} оплачен
                </p>
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-sm text-green-800">
                        Чек отправлен на вашу почту
                    </p>
                </div>
            </div>
        );
    }

    // Ошибка
    if (status === 'error') {
        return (
            <div className="flex flex-col items-center justify-center py-8">
                <XCircle className="text-red-500 mb-4" size={64} />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Ошибка оплаты
                </h3>

                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 w-full">
                    <p className="text-sm text-red-800 text-center">
                        {error || 'Не удалось выполнить платеж'}
                    </p>
                    {error?.code && (
                        <p className="text-xs text-red-700 text-center mt-1">
                            Код: {error.code}
                        </p>
                    )}
                </div>

                <div className="flex gap-3 w-full">
                    <button
                        onClick={onRetry}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg font-medium"
                    >
                        Попробовать снова
                    </button>
                    <button
                        onClick={onCancel}
                        className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 px-4 rounded-lg font-medium"
                    >
                        Назад
                    </button>
                </div>

                <div className="mt-4 text-xs text-gray-500 text-center">
                    Если ошибка повторяется, попробуйте другую карту<br />
                    или свяжитесь с банком
                </div>
            </div>
        );
    }

    return null;
};

export default CloudPaymentsWidget;